import { Menu } from "lucide-react";
import type { ReactNode } from "react";
import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";

import { Command, CommandInput } from "@nexus/ui";
import { useAppLayout } from "@/hooks/useAppLayout";
import { cn } from "@/lib/utils";
import { getSearchShortcutLabel, useGlobalSearch } from "./GlobalSearchContext";
import { GlobalSearchResults } from "./GlobalSearchDialog";
import { MobileNavigationDrawer } from "./MobileNavigationDrawer";
import { AdminMenu } from "./AdminMenu";

/** Search stays in place; results expand below it without obscuring the page. */
export function GlobalSearchBar({
  breadcrumb,
  searchEnabled = true,
}: {
  breadcrumb?: ReactNode;
  searchEnabled?: boolean;
}) {
  const { open, setOpen, openSearch } = useGlobalSearch();
  const { layoutMode } = useAppLayout();
  const { pathname } = useLocation();
  const isHome = pathname === "/home";
  const [navigationOpen, setNavigationOpen] = useState(false);
  const [value, setValue] = useState("");
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    function onPointerDown(event: PointerEvent) {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    }
    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, [open, setOpen]);

  useEffect(() => {
    setOpen(false);
    setValue("");
  }, [pathname, setOpen]);

  function closeSearch() {
    setOpen(false);
    setValue("");
    inputRef.current?.blur();
  }

  return (
    <>
      <div
        data-global-search-bar
        className={cn(
          "sticky top-0 z-40 flex h-14 shrink-0 items-center gap-3 px-3 text-primary md:px-(--density-page-x)",
          isHome ? "bg-surface" : "border-b border-border bg-background",
        )}
      >
        <div className="relative z-10 flex h-full min-w-0 shrink-0 items-center gap-2 overflow-hidden md:flex-1">
          <button
            type="button"
            aria-label="Open navigation"
            aria-expanded={navigationOpen}
            onClick={() => setNavigationOpen(true)}
            className={cn(
              "size-8 shrink-0 items-center justify-center rounded-md hover:bg-surface-elevated focus-visible:outline-2 focus-visible:outline-focus",
              layoutMode === "workspace" ? "flex xl:hidden" : "flex md:hidden",
            )}
          >
            <Menu className="size-4" aria-hidden />
          </button>
          {breadcrumb ? <div className="hidden min-w-0 flex-1 md:flex">{breadcrumb}</div> : null}
        </div>
        {searchEnabled ? (
          <div
            ref={rootRef}
            className="relative z-10 ml-auto h-10 w-full max-w-80 min-w-0 self-center"
          >
            <Command
              shouldFilter={false}
              loop
              label="Global search"
              className="h-10 overflow-visible bg-transparent"
            >
              <CommandInput
                ref={inputRef}
                value={value}
                onValueChange={(next) => {
                  setValue(next);
                  if (!open) openSearch();
                }}
                onFocus={openSearch}
                onKeyDown={(event) => {
                  if (event.key === "Escape") {
                    event.preventDefault();
                    closeSearch();
                  }
                }}
                placeholder="Search…"
                aria-label="Global search"
                aria-expanded={open}
                aria-keyshortcuts="/ Meta+K Control+K"
                wrapperClassName="h-10 rounded-control border border-control-border bg-surface pl-3 pr-16 text-muted transition-colors hover:border-control-border-hover focus-within:border-focus focus-within:ring-1 focus-within:ring-inset focus-within:ring-focus"
                className="h-full text-[15px] placeholder:text-sm"
              />
              <kbd className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 rounded border border-border px-1.5 py-0.5 text-[10px] text-muted">
                {getSearchShortcutLabel()}
              </kbd>
              {open ? (
                <div
                  data-global-search-results
                  role="region"
                  aria-label="Search results"
                  className="absolute inset-x-0 top-full mt-1 overflow-hidden rounded-lg border border-border bg-surface shadow-popover"
                >
                  <GlobalSearchResults value={value} onClose={closeSearch} />
                </div>
              ) : null}
            </Command>
          </div>
        ) : null}
        <div className="relative z-10 flex h-full min-w-0 shrink-0 items-center justify-end empty:hidden">
          <AdminMenu />
        </div>
      </div>
      <MobileNavigationDrawer open={navigationOpen} onClose={() => setNavigationOpen(false)} />
    </>
  );
}
